import React, { useState, useEffect } from 'react';
import { lcoFeaturesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function LCOFeatures() {
  const { tenant } = useAuth();
  const primary = tenant?.branding?.primary_color || '#1B6B3A';
  const [lcos, setLcos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState('');
  const [filterEnabled, setFilterEnabled] = useState('all');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await lcoFeaturesAPI.getAll();
      setLcos(r.data.data||[]);
    } catch(e){ console.error(e); }
    setLoading(false);
  };

  const toggle = async (lco_code, feature, current, features) => {
    setSaving(s=>({...s,[lco_code+feature]:true}));
    try {
      const updated = {...features, [feature]:!current};
      await lcoFeaturesAPI.update(lco_code, updated);
      setLcos(prev=>prev.map(l=>l.lco_code===lco_code?{...l,features:{...l.features,[feature]:!current}}:l));
    } catch(e){ alert('Error: '+e.message); }
    setSaving(s=>({...s,[lco_code+feature]:false}));
  };

  const enableAll = async (lco_code, features) => {
    setSaving(s=>({...s,[lco_code+'all']:true}));
    try {
      const updated = {collections:true,expenses:true,inventory:true,staff:true};
      await lcoFeaturesAPI.update(lco_code, updated);
      setLcos(prev=>prev.map(l=>l.lco_code===lco_code?{...l,features:{...l.features,...updated}}:l));
    } catch(e){ alert('Error: '+e.message); }
    setSaving(s=>({...s,[lco_code+'all']:false}));
  };

  const disableAll = async (lco_code) => {
    if(!window.confirm('Disable all features for '+lco_code+'?')) return;
    setSaving(s=>({...s,[lco_code+'all']:true}));
    try {
      const updated = {collections:false,expenses:false,inventory:false,staff:false};
      await lcoFeaturesAPI.update(lco_code, updated);
      setLcos(prev=>prev.map(l=>l.lco_code===lco_code?{...l,features:{...l.features,...updated}}:l));
    } catch(e){ alert('Error: '+e.message); }
    setSaving(s=>({...s,[lco_code+'all']:false}));
  };

  const isEnabled = (f) => f.collections||f.expenses||f.inventory||f.staff;

  const filtered = lcos.filter(l=>{
    if(search && !l.lco_code.toLowerCase().includes(search.toLowerCase())) return false;
    if(filterEnabled==='enabled' && !isEnabled(l.features)) return false;
    if(filterEnabled==='disabled' && isEnabled(l.features)) return false;
    return true;
  });

  const Toggle = ({lco_code, feature, value, features}) => (
    <button onClick={()=>toggle(lco_code, feature, value, features)}
      disabled={saving[lco_code+feature]||saving[lco_code+'all']}
      style={{width:'44px',height:'24px',borderRadius:'12px',border:'none',cursor:'pointer',
        background:value?primary:'#e2e8f0',position:'relative',transition:'all 0.2s',
        opacity:saving[lco_code+feature]?0.6:1}}>
      <div style={{position:'absolute',top:'2px',left:value?'22px':'2px',width:'20px',height:'20px',
        background:'white',borderRadius:'50%',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}/>
    </button>
  );

  const enabledCount = lcos.filter(l=>isEnabled(l.features)).length;

  return (
    <div style={{padding:'20px',maxWidth:'1200px',margin:'0 auto'}}>
      {/* Header */}
      <div style={{marginBottom:'20px'}}>
        <h2 style={{margin:0,fontSize:'20px',fontWeight:'800',color:'#0f172a'}}>🎛️ LCO Feature Management</h2>
        <p style={{margin:'4px 0 0',fontSize:'12px',color:'#64748b'}}>
          Enable or disable pilot features per LCO — data remains private to each LCO
        </p>
      </div>

      {/* Summary cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'12px',marginBottom:'20px'}}>
        {[
          {l:'Total LCOs',v:lcos.length,c:'#2563eb',bg:'#eff6ff',f:'all'},
          {l:'Features Enabled',v:enabledCount,c:'#16a34a',bg:'#f0fdf4',f:'enabled'},
          {l:'Not Enabled',v:lcos.length-enabledCount,c:'#94a3b8',bg:'#f8fafc',f:'disabled'},
          {l:'Collections Active',v:lcos.filter(l=>l.features?.collections).length,c:'#f59e0b',bg:'#fffbeb',f:'enabled'},
        ].map(s=>(
          <div key={s.l} onClick={()=>setFilterEnabled(s.f)}
            style={{background:s.bg,borderRadius:'10px',padding:'14px',textAlign:'center',
              border:'2px solid',borderColor:filterEnabled===s.f?s.c:'#f1f5f9',
              cursor:'pointer',transition:'all 0.15s'}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=s.c}
            onMouseLeave={e=>e.currentTarget.style.borderColor=filterEnabled===s.f?s.c:'#f1f5f9'}>
            <div style={{fontSize:'22px',fontWeight:'900',color:s.c}}>{s.v}</div>
            <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>{s.l}</div>
            <div style={{fontSize:'9px',color:'#cbd5e1',marginTop:'2px'}}>tap to filter ▶</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:'8px',marginBottom:'16px',flexWrap:'wrap',alignItems:'center'}}>
        <input type="text" value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 Search LCO code..."
          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',minWidth:'200px'}}/>
        <select value={filterEnabled} onChange={e=>setFilterEnabled(e.target.value)}
          style={{padding:'8px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px'}}>
          <option value="all">All LCOs</option>
          <option value="enabled">Features Enabled</option>
          <option value="disabled">Not Enabled</option>
        </select>
        <span style={{fontSize:'12px',color:'#64748b',marginLeft:'auto'}}>
          Showing {filtered.length} of {lcos.length} LCOs
        </span>
      </div>

      {/* Table */}
      {loading ? <div style={{textAlign:'center',padding:'40px',color:'#94a3b8'}}>Loading...</div> : (
        <div style={{background:'white',borderRadius:'12px',border:'1px solid #f1f5f9',overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
            <thead><tr style={{background:'#f8fafc'}}>
              <th style={{padding:'12px 16px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>LCO Code</th>
              <th style={{padding:'12px 16px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Customers</th>
              <th style={{padding:'12px 16px',textAlign:'center',fontWeight:'700',color:'#f59e0b'}}>💰 Collections</th>
              <th style={{padding:'12px 16px',textAlign:'center',fontWeight:'700',color:'#16a34a'}}>📒 Expenses</th>
              <th style={{padding:'12px 16px',textAlign:'center',fontWeight:'700',color:'#2563eb'}}>📦 Inventory</th>
              <th style={{padding:'12px 16px',textAlign:'center',fontWeight:'700',color:'#7c3aed'}}>👥 Staff</th>
              <th style={{padding:'12px 16px',textAlign:'center',fontWeight:'700',color:'#64748b'}}>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.length===0 ? (
                <tr><td colSpan={7} style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>No LCOs found</td></tr>
              ) : filtered.map((l,i)=>(
                <tr key={i} style={{borderTop:'1px solid #f1f5f9',background:i%2?'#fafafa':'white'}}>
                  <td style={{padding:'12px 16px'}}>
                    <div style={{fontWeight:'800',color:'#0f172a'}}>{l.lco_code}</div>
                    {isEnabled(l.features) && (
                      <span style={{fontSize:'10px',background:'#f0fdf4',color:'#16a34a',padding:'1px 6px',borderRadius:'10px',fontWeight:'600'}}>
                        ✅ Active
                      </span>
                    )}
                  </td>
                  <td style={{padding:'12px 16px',textAlign:'right',fontWeight:'600',color:'#64748b'}}>{l.customer_count}</td>
                  <td style={{padding:'12px 16px',textAlign:'center'}}>
                    <Toggle lco_code={l.lco_code} feature="collections" value={l.features?.collections} features={l.features}/>
                  </td>
                  <td style={{padding:'12px 16px',textAlign:'center'}}>
                    <Toggle lco_code={l.lco_code} feature="expenses" value={l.features?.expenses} features={l.features}/>
                  </td>
                  <td style={{padding:'12px 16px',textAlign:'center'}}>
                    <Toggle lco_code={l.lco_code} feature="inventory" value={l.features?.inventory} features={l.features}/>
                  </td>
                  <td style={{padding:'12px 16px',textAlign:'center'}}>
                    <Toggle lco_code={l.lco_code} feature="staff" value={l.features?.staff} features={l.features}/>
                  </td>
                  <td style={{padding:'12px 16px',textAlign:'center'}}>
                    <div style={{display:'flex',gap:'4px',justifyContent:'center'}}>
                      <button onClick={()=>enableAll(l.lco_code, l.features)}
                        disabled={saving[l.lco_code+'all']}
                        style={{padding:'4px 8px',background:'#f0fdf4',color:'#16a34a',border:'1px solid #bbf7d0',
                          borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>
                        Enable All
                      </button>
                      <button onClick={()=>disableAll(l.lco_code)}
                        disabled={saving[l.lco_code+'all']}
                        style={{padding:'4px 8px',background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca',
                          borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>
                        Disable All
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
